import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Switch, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

/**
 * Exactly ONE of tierName / freeMinutes / vacMinutes / vendCents may be set
 * per row (backend zod refine). tierName → free wash; freeMinutes →
 * self-serve minutes; vacMinutes → vacuum minutes (1–120); vendCents →
 * vending credit (25–10000 cents).
 */
interface RedemptionEntry {
  points: number;
  tierId?: string | null;
  tierName: string | null;
  freeMinutes: number | null;
  vacMinutes?: number | null;
  vendCents?: number | null;
}
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }
interface PointsCfg {
  ownerId: string;
  isEnabled: boolean;
  pointsPerDollar: number;
  redemptionTable: RedemptionEntry[];
  expirationMonths: number;
  customerVisibleName: string | null;
}

export default function PointsSettingsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();

  const [cfg, setCfg] = useState<PointsCfg | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);
  // Raw dollar text for vending-credit rows so decimals type naturally
  // ("2." wouldn't survive a cents round-trip). Keyed by row index.
  const [vendTexts, setVendTexts] = useState<Record<number, string>>({});

  useEffect(() => {
    (async () => {
      try {
        const c = await apiGet<PointsCfg>('/api/owner/points/config');
        setCfg(c);
        // Wash tiers come from the owner's catalog (source of truth).
        const catalog = await apiGet<WashTier[]>('/api/wash-tiers');
        setWashTiers(catalog.filter(t => t.isActive));
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
  }, []);

  if (!cfg) return null;

  const update = (patch: Partial<PointsCfg>) => setCfg({ ...cfg, ...patch });

  const addRow = (kind: 'tier' | 'minutes' | 'vacMinutes' | 'vendCents') => {
    if (kind === 'tier') {
      const first = washTiers[0];
      if (!first) { toast.show({ type: 'error', title: 'No wash tiers', message: 'Create wash tiers under "Manage Wash Tiers" first.' }); return; }
      update({ redemptionTable: [...cfg.redemptionTable, { points: 500, tierId: first.id, tierName: first.name, freeMinutes: null, vacMinutes: null, vendCents: null }] });
    } else if (kind === 'vacMinutes') {
      update({ redemptionTable: [...cfg.redemptionTable, { points: 200, tierId: null, tierName: null, freeMinutes: null, vacMinutes: 10, vendCents: null }] });
    } else if (kind === 'vendCents') {
      update({ redemptionTable: [...cfg.redemptionTable, { points: 200, tierId: null, tierName: null, freeMinutes: null, vacMinutes: null, vendCents: 200 }] });
    } else {
      update({ redemptionTable: [...cfg.redemptionTable, { points: 300, tierId: null, tierName: null, freeMinutes: 5, vacMinutes: null, vendCents: null }] });
    }
  };
  const removeRow = (idx: number) => {
    // Reset the vend $ input texts — indexes shift after a removal.
    setVendTexts({});
    update({ redemptionTable: cfg.redemptionTable.filter((_, i) => i !== idx) });
  };
  const editRow = (idx: number, patch: Partial<RedemptionEntry>) => {
    update({
      redemptionTable: cfg.redemptionTable.map((r, i) => i === idx ? { ...r, ...patch } : r),
    });
  };

  const handleSave = async () => {
    // Range checks the backend enforces (vacMinutes 1–120, vendCents 25–10000).
    for (const r of cfg.redemptionTable) {
      if (r.vacMinutes != null && (r.vacMinutes < 1 || r.vacMinutes > 120)) {
        toast.show({ type: 'error', title: 'Invalid vacuum minutes', message: 'Vacuum minutes must be 1–120.' });
        return;
      }
      if (r.vendCents != null && r.vendCents !== 0 && (r.vendCents < 25 || r.vendCents > 10000)) {
        toast.show({ type: 'error', title: 'Invalid vending credit', message: 'Vending credit must be $0.25–$100.' });
        return;
      }
    }
    try {
      setSubmitting(true);
      await api('/api/owner/points/config', {
        method: 'PUT',
        body: {
          isEnabled: cfg.isEnabled,
          pointsPerDollar: Number(cfg.pointsPerDollar) || 1.0,
          redemptionTable: cfg.redemptionTable.filter(r =>
            r.points > 0 && (r.tierName || r.freeMinutes || r.vacMinutes || r.vendCents)),
          expirationMonths: cfg.expirationMonths || 12,
          customerVisibleName: cfg.customerVisibleName || null,
        },
      });
      toast.show({ type: 'success', title: 'Saved' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Card style={styles.formCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionTitle}>Enable rewards program</Text>
              <Text style={styles.hintText}>Customers earn points on paid washes and redeem for free washes.</Text>
            </View>
            <Switch value={cfg.isEnabled} onValueChange={(v) => update({ isEnabled: v })} />
          </View>
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Earn rate</Text>
          <Input
            label="Points per $1 spent"
            value={String(cfg.pointsPerDollar)}
            onChangeText={(t) => update({ pointsPerDollar: parseFloat(t) || 0 })}
            keyboardType="decimal-pad"
            placeholder="1"
          />
          <Input
            label="Program name (shown to customers)"
            value={cfg.customerVisibleName ?? ''}
            onChangeText={(t) => update({ customerVisibleName: t || null })}
            placeholder="e.g. Kraken Rewards"
          />
          <Input
            label="Points expire after (months)"
            value={String(cfg.expirationMonths)}
            onChangeText={(t) => update({ expirationMonths: parseInt(t) || 12 })}
            keyboardType="number-pad"
            placeholder="12"
          />
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Redemption options</Text>
          {cfg.redemptionTable.length === 0 && (
            <Text style={styles.hintText}>Add what customers can redeem points for. Each row is one offer.</Text>
          )}
          {cfg.redemptionTable.map((row, idx) => (
            <View key={idx} style={styles.rowEditor}>
              <View style={{ flex: 1, gap: spacing.xs }}>
                <Input
                  label="Points"
                  value={String(row.points)}
                  onChangeText={(t) => editRow(idx, { points: parseInt(t) || 0 })}
                  keyboardType="number-pad"
                />
                {row.tierName !== null ? (
                  washTiers.length > 0 ? (
                    <Picker
                      label="Wash tier"
                      options={washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }))}
                      value={row.tierId ?? ''}
                      onValueChange={(v) => { const t = washTiers.find(x => x.id === v); editRow(idx, { tierId: v, tierName: t?.name ?? row.tierName }); }}
                    />
                  ) : (
                    <Text style={styles.hintText}>Create wash tiers under "Manage Wash Tiers" first.</Text>
                  )
                ) : row.vacMinutes != null ? (
                  <Input
                    label="Vacuum minutes (1–120)"
                    value={String(row.vacMinutes ?? 0)}
                    onChangeText={(t) => editRow(idx, { vacMinutes: parseInt(t) || 0 })}
                    keyboardType="number-pad"
                  />
                ) : row.vendCents != null ? (
                  <Input
                    label="Vending credit ($0.25–$100)"
                    value={vendTexts[idx] ?? String((row.vendCents ?? 0) / 100)}
                    onChangeText={(t) => {
                      setVendTexts(prev => ({ ...prev, [idx]: t }));
                      editRow(idx, { vendCents: Math.round((parseFloat(t) || 0) * 100) });
                    }}
                    keyboardType="decimal-pad"
                  />
                ) : (
                  <Input label="Free self-serve minutes" value={String(row.freeMinutes ?? 0)} onChangeText={(t) => editRow(idx, { freeMinutes: parseInt(t) || 0 })} keyboardType="number-pad" />
                )}
              </View>
              <TouchableOpacity onPress={() => removeRow(idx)} style={styles.removeBtn}>
                <Ionicons name="trash" size={20} color={colors.danger} />
              </TouchableOpacity>
            </View>
          ))}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm }}>
            <TouchableOpacity onPress={() => addRow('tier')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add auto tier reward</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => addRow('minutes')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add bay minutes</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => addRow('vacMinutes')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add vacuum minutes</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => addRow('vendCents')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add vending credit</Text>
            </TouchableOpacity>
          </View>
        </Card>

        <Button title="Save" onPress={handleSave} loading={submitting} size="lg" />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.sm },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic' },
  rowEditor: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.md },
  removeBtn: { padding: spacing.sm, justifyContent: 'center' },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.primary },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
